import { useState } from 'react';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { storage } from '../lib/firebase';

interface UploadProgress {
  progress: number;
  uploading: boolean;
  error: string | null;
  downloadURL: string | null;
}

export const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;

// Extension drives the content type: browsers report inconsistent types for
// .doc/.txt/.html, and storage.rules matches on content type (IS-1714).
const CONTENT_TYPE_BY_EXTENSION: Record<string, string> = {
  pdf: 'application/pdf',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  txt: 'text/plain',
  html: 'text/html',
  htm: 'text/html',
};

export const ACCEPTED_UPLOAD_EXTENSIONS = Object.keys(CONTENT_TYPE_BY_EXTENSION);

function extensionOf(fileName: string): string {
  const parts = fileName.toLowerCase().split('.');
  return parts.length > 1 ? parts[parts.length - 1] : '';
}

// Header-safe filename for Content-Disposition
function sanitizeFileName(fileName: string): string {
  return fileName.replace(/[^\w.\- ]+/g, '_').slice(0, 120);
}

export function validateUploadFile(file: File): string | null {
  const extension = extensionOf(file.name);
  if (!CONTENT_TYPE_BY_EXTENSION[extension]) {
    return `${file.name} is not a supported file type. Upload PDF, DOC, DOCX, TXT, or HTML.`;
  }
  if (file.size >= MAX_UPLOAD_BYTES) {
    return `${file.name} is larger than 10MB. Please upload a smaller file.`;
  }
  return null;
}

export function useFileUpload() {
  const [uploadState, setUploadState] = useState<UploadProgress>({
    progress: 0,
    uploading: false,
    error: null,
    downloadURL: null,
  });

  const uploadFile = async (
    file: File,
    jiraKey: string,
    fieldName: string
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const validationError = validateUploadFile(file);
      if (validationError) {
        setUploadState({
          progress: 0,
          uploading: false,
          error: validationError,
          downloadURL: null,
        });
        reject(new Error(validationError));
        return;
      }

      // Reset state
      setUploadState({
        progress: 0,
        uploading: true,
        error: null,
        downloadURL: null,
      });

      // Create a storage reference
      const timestamp = Date.now();
      const safeName = sanitizeFileName(file.name);
      const fileName = `${timestamp}_${safeName}`;
      const storageRef = ref(storage, `projects/${jiraKey}/${fieldName}/${fileName}`);

      // Always download rather than render: uploaded legal docs can be HTML
      const uploadTask = uploadBytesResumable(storageRef, file, {
        contentType: CONTENT_TYPE_BY_EXTENSION[extensionOf(file.name)],
        contentDisposition: `attachment; filename="${safeName}"`,
      });

      uploadTask.on(
        'state_changed',
        (snapshot) => {
          // Track progress
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadState((prev) => ({
            ...prev,
            progress,
          }));
          console.log(`📤 Upload progress: ${progress.toFixed(1)}%`);
        },
        (error) => {
          // Handle error
          console.error('❌ Upload error:', error);
          setUploadState({
            progress: 0,
            uploading: false,
            error: error.message,
            downloadURL: null,
          });
          reject(error);
        },
        async () => {
          // Upload completed successfully
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log('✅ File uploaded successfully:', downloadURL);
            setUploadState({
              progress: 100,
              uploading: false,
              error: null,
              downloadURL,
            });
            resolve(downloadURL);
          } catch (error) {
            console.error('❌ Error getting download URL:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            setUploadState({
              progress: 0,
              uploading: false,
              error: errorMessage,
              downloadURL: null,
            });
            reject(error);
          }
        }
      );
    });
  };

  const resetUploadState = () => {
    setUploadState({
      progress: 0,
      uploading: false,
      error: null,
      downloadURL: null,
    });
  };

  return {
    uploadFile,
    uploadState,
    resetUploadState,
  };
}

